import { DollarSign, Package, Activity, Palette, Calendar } from "lucide-react";
import { formatCurrency } from "@/utils/currency";
import { format, parseISO } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Safe date formatting
const formatDateRange = (dateStr) => {
  try {
    return format(parseISO(dateStr), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
};

export default function StatsCards({ stats, statsMonth, setStatsMonth, monthOptions }) {
  if (!stats) return null;

  const cards = [
    {
      title: "Total Revenue",
      value: formatCurrency(stats.total_revenue),
      icon: DollarSign,
      color: "text-green-600",
      bgColor: "bg-green-50",
      testId: "total-revenue-stat"
    },
    {
      title: "Total Projects",
      value: stats.total_projects,
      icon: Package,
      color: "text-blue-600",
      bgColor: "bg-blue-50",
      testId: "total-projects-stat"
    },
    {
      title: "In Progress",
      value: stats.in_progress,
      icon: Activity,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      testId: "in-progress-stat"
    },
    {
      title: "Sublimation / Embroidery",
      value: `${stats.sublimation_count} / ${stats.embroidery_count}`,
      icon: Palette,
      color: "text-purple-600",
      bgColor: "bg-purple-50",
      testId: "methods-stat"
    },
  ];

  // Get date range display text
  const getDateRangeText = () => {
    if (statsMonth === "all" && stats.date_range) {
      const start = formatDateRange(stats.date_range.start);
      const end = formatDateRange(stats.date_range.end);
      if (start === end) {
        return `Orders from ${start}`;
      }
      return `Orders from ${start} to ${end}`;
    }
    if (statsMonth && statsMonth !== "all") {
      return `Showing stats for ${monthOptions?.find(o => o.value === statsMonth)?.label || statsMonth}`;
    }
    return null;
  };

  const dateRangeText = getDateRangeText();

  return (
    <div>
      {/* Month Filter */}
      {monthOptions && setStatsMonth && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-heading font-semibold text-muted-foreground">
            Statistics Overview
          </h2>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <Select value={statsMonth} onValueChange={setStatsMonth}>
              <SelectTrigger className="w-48" data-testid="stats-month-filter">
                <SelectValue placeholder="Select month" />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Stats Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {cards.map((card, index) => {
          const Icon = card.icon;
          return (
            <div
              key={index}
              className="stat-card bg-card border border-border rounded-md p-6"
              data-testid={card.testId}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`${card.bgColor} ${card.color} p-3 rounded-md`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
              <h3 className="text-sm font-medium text-muted-foreground mb-1">{card.title}</h3>
              <p className="text-2xl font-heading font-bold tracking-tight text-foreground">
                {card.value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Show date range indicator */}
      {dateRangeText && (
        <p className="text-xs text-muted-foreground mt-2 text-right" data-testid="date-range-indicator">
          {dateRangeText}
        </p>
      )}
    </div>
  );
}
